import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';

import { AuthService } from '@data/services/modules/auth/auth.service';
import { ApiQuoteParametersService } from '../api/api-quote-parameters.service';
import { IQuoteParameters, ICreateQuoteParameters, IUpdateQuoteParameters } from '../../interfaces/iquote-parameters.interface';
import { FilterOptions } from '@shared/interfaces/filterOptions.interface';
import { UserRole } from '@data/enums/user-role.enum';
import { ALLOWED_FINANCING_TERMS, FINANCING_TERM_OPTIONS, financingTermOptionsFor } from '@data/constants/financing-terms.constant';

@Injectable({
  providedIn: 'root'
})
export class QuoteParametersService {
  entity: FormGroup = this.fb.group({});
  role!: string
  formMethod = 'CREATE'

  // ADR-0006: cuando el actor es Broker, el cotizador SIEMPRE opera con datos
  // provisionales (no expone clientes/activos reales). El toggle "Datos Reales"
  // se oculta en la UI y los setters externos quedan ignorados via el setter.
  private _useProvisionalData = false
  get useProvisionalData(): boolean {
    return this.isBroker ? true : this._useProvisionalData
  }
  set useProvisionalData(value: boolean) {
    if (this.isBroker) return
    this._useProvisionalData = value
  }

  // Activo provisional, INDEPENDIENTE del cliente: permite cotizar a un cliente real
  // usando un activo provisional (nombre + valor) sin registrarlo. Broker → siempre true.
  private _useProvisionalAsset = false
  get useProvisionalAsset(): boolean {
    return this.isBroker ? true : this._useProvisionalAsset
  }
  set useProvisionalAsset(value: boolean) {
    if (this.isBroker) return
    this._useProvisionalAsset = value
  }

  get isBroker(): boolean {
    return this.authService.getUser?.user?.permissions?.name === UserRole.BROKER
  }

  // Se lee en vivo y no de `this.role`: el servicio es singleton y `role` se fija una sola vez,
  // así que tras cambiar de sesión ofrecería los plazos del usuario anterior.
  private get termOptions(): { label: string; value: number; disabled?: boolean }[] {
    return financingTermOptionsFor(this.authService.getUser?.user?.permissions?.name)
  }

  quote!: any
  customers: { label: string; value: string; rfc?: string }[] = []
  actives: { label: string; value: string; amount: number }[] = []
  sectors: { label: string; value: string; }[] = []
  // Plazos permitidos: 36/48/60 meses, más 12/24 solo para Admin (ver `termOptions`).
  // Source of truth en @data/constants/financing-terms.constant.
  // En modo edición se inyectan aquí los plazos legacy de la cotización como
  // opciones `disabled` (ver populateEditForm) para mostrarlos sin permitir
  // re-selección.
  termInMonths: { label: string; value: number; disabled?: boolean }[] = [...FINANCING_TERM_OPTIONS]
  kindLeases: { label: string; value: string; }[] = [
    { label: "Puro", value: "Puro" }
    // Financiero eliminado temporalmente
  ]
  termsArray: FormArray = this.fb.array([]); // {term: number, residuary: number}
  accessories!: FormArray

  private quoteAmountsSubject = new BehaviorSubject<any[]>([{
    term: 0,
    irr: 0,
    advance: 0,
    openingCommission: 0,
    advancedRents: 0,
    advancedRentsNumber: 0,
    advancedRentsAccessories: 0,
    advancedRentsIva: 0,
    advancedRentsTotal: 0,
    ratificationExpenses: 0,
    accessories: [],
    advanceSubtotal: 0,
    advanceIva: 0,
    advanceTotal: 0,
    monthlyCapital: 0,
    monthlyInterest: 0,
    monthlyAccesories: [],
    monthlySubtotal: 0,
    monthlyIva: 0,
    monthlyTotal: 0,
    residuary: 0,
    residuaryIva: 0,
    residuaryTotal: 0,
  }]);
  public quoteAmounts$ = this.quoteAmountsSubject.asObservable();

  constructor(
    private fb: FormBuilder,
    private apiQuoteParametersService: ApiQuoteParametersService,
    private authService: AuthService
  ) {
    this.searchClients('')
    this.fetchAssets({ page: 1, limit: 10, filters: {} })
    this.role = this.apiQuoteParametersService.user

    this.entity = this.fb.group({});

    this.termsArray = this.fb.array([]);
    this.entity.addControl('termsArray', this.termsArray);
  }

  private readonly defaultQuoteAmounts = [{
    term: 0,
    irr: 0,
    advance: 0,
    openingCommission: 0,
    advancedRents: 0,
    advancedRentsNumber: 0,
    advancedRentsAccessories: 0,
    advancedRentsIva: 0,
    advancedRentsTotal: 0,
    ratificationExpenses: 0,
    accessories: [],
    advanceSubtotal: 0,
    advanceIva: 0,
    advanceTotal: 0,
    monthlyCapital: 0,
    monthlyInterest: 0,
    monthlyAccesories: [],
    monthlySubtotal: 0,
    monthlyIva: 0,
    monthlyTotal: 0,
    residuary: 0,
    residuaryIva: 0,
    residuaryTotal: 0,
  }];

  updateQuoteAmounts(newQuoteAmounts: any[]) {
    this.quoteAmountsSubject.next(newQuoteAmounts);
  }

  resetQuoteAmounts() {
    this.quoteAmountsSubject.next(this.defaultQuoteAmounts);
  }

  newEntity() {
    // Este método ahora delega al nuevo sistema unificado
    this.initializeForm();
  }

  get termsArrayControls() {
    // Devuelve los controles del FormArray como un array para iterarlos en la plantilla
    return this.termsArray.controls as FormGroup[];
  }

  setFormMethod(method: string) {
    this.formMethod = method;
  }

  /**
   * F-016: valor de `advancedRentsIncludeAccessories` para la vista previa (`POST /quotation-parameters/calculate`).
   * Una cotización nueva cobra la renta anticipada completa —renta del activo + mensualidad de los accesorios
   * financiados— y por eso va en `true`. Al editar se manda la bandera que la cotización ya tiene guardada, para que
   * la vista previa de una cotización vieja no cambie de montos solo por recalcularla.
   * Va ÚNICAMENTE en la vista previa: el DTO de alta/edición la rechaza (forbidNonWhitelisted → 400).
   */
  advancedRentsIncludeAccessoriesForPreview(): boolean {
    if (this.formMethod !== 'EDIT' || !this.quote) return true;
    return this.quote.advancedRentsIncludeAccessories === true;
  }

  getCustomerName(id: string) {
    return this.customers.find(c => c.value === id)?.label
  }

  getActiveName(id: string) {
    return this.actives.find(c => c.value === id)?.label
  }

  getSectorName(id: string) {
    return this.sectors.find(c => c.value === id)?.label
  }

  getKindLeaseName(id: string) {
    return this.kindLeases.find(c => c.value === id)?.label
  }

  /**
   * Maneja cambios en la selección de plazos preservando los valores de residuary existentes
   * @param selectedTerms Array de plazos seleccionados (en meses)
   */
  onTermsChange(selectedTerms: number[]): void {
    // Preservar los valores de residuary existentes antes de limpiar
    const existingResiduaryValues: Map<number, number | null> = new Map();
    this.termsArray.controls.forEach((control) => {
      const term = control.get('term')?.value;
      const residuary = control.get('residuary')?.value;
      if (term !== undefined) {
        existingResiduaryValues.set(term, residuary);
      }
    });

    // Limpiar y recrear el array preservando valores existentes
    this.termsArray.clear();
    selectedTerms.forEach((term) => {
      const existingResiduary = existingResiduaryValues.get(term);
      this.addTermWithResiduary(term, existingResiduary ?? null);
    });

    // Actualiza el control 'terms' en el formulario principal para reflejar los términos seleccionados
    this.entity.get('terms')?.setValue(selectedTerms);
  }

  /**
   * Agrega un término con un valor de residuary específico
   * @param term Plazo en meses
   * @param residuary Valor del residuary (porcentaje) o null para nuevo
   */
  addTermWithResiduary(term: number, residuary: number | null): void {
    const minResiduary = this.role === UserRole.BROKER ? 5 : 0;
    const maxResiduary = this.role === UserRole.BROKER ? 30 : 100;
    
    this.termsArray.push(
      this.fb.group({
        term: [term, Validators.required],
        residuary: [residuary, [Validators.required, Validators.min(minResiduary), Validators.max(maxResiduary)]],
      })
    );
  }

  /**
   * Agrega un nuevo término con residuary vacío (para nuevos plazos)
   * @param term Plazo en meses
   */
  addTerm(term: number): void {
    this.addTermWithResiduary(term, null);
  }

  getTermInMonths(value: number | number[]): string {
    if (Array.isArray(value)) {
      return value.map(val => this.termInMonths.find(term => term.value === val)?.label ?? `${val}`).join(', ');
    } else {
      // Fallback a `${value}` para no dejar label vacío en cotizaciones
      // históricas con plazos ya retirados del catálogo.
      return this.termInMonths.find(term => term.value === value)?.label ?? `${value}`;
    }
  }

  /**
   * Candado de plazos permitidos. Un plazo es válido para submit si es una
   * opción vigente del catálogo (36/48/60, y 12/24 para Admin) o un plazo legacy inyectado como
   * opción deshabilitada al editar una cotización histórica.
   */
  isTermAllowed(term: number): boolean {
    return ALLOWED_FINANCING_TERMS.includes(term) || this.termInMonths.some(o => o.value === term);
  }

  /**
   * Inyecta en el catálogo de opciones los plazos de una cotización en edición
   * que el rol no puede elegir (14/18/72, y 12/24 si no es Admin), marcados como `disabled`, para
   * que el multiSelect los muestre como seleccionados sin permitir re-elegirlos.
   */
  private injectLegacyTermOptions(terms: number[]): void {
    this.termInMonths = this.termOptions;
    for (const term of terms) {
      if (!this.termInMonths.some(o => o.value === term)) {
        this.termInMonths.push({ label: `${term}`, value: term, disabled: true });
      }
    }
  }

  // Búsqueda server-side de clientes (nombre O RFC). Preserva el cliente ya seleccionado
  // en `customers` aunque no venga en los resultados, para que su etiqueta no desaparezca
  // del dropdown al cambiar el término de búsqueda.
  searchClients(q = '') {
    const selectedId = this.entity?.get('client')?.value
    const selected = selectedId ? this.customers.find(c => c.value === selectedId) : null

    this.apiQuoteParametersService.searchClients(q).subscribe(
      clients => {
        const merged = [...clients]
        if (selected && !merged.some(c => c.value === selected.value)) merged.unshift(selected)
        this.customers = merged
      },
      error => {
        console.error('Error al buscar los clientes:', error);
      }
    );
  }

  fetchAssets(filterOptions: FilterOptions) {
    this.apiQuoteParametersService.fetchAssets(filterOptions).subscribe(
      (assets: any[]) => {
        this.actives = assets
      },
      error => {
        console.error('Error al obtener los activos:', error);
      }
    );
  }

  /**
   * Método para cambiar entre modo provisional y real sin perder datos
   */
  toggleDataMode(useProvisional: boolean): void {
    this.useProvisionalData = useProvisional;
    this.updateFormValidations();
  }

  /** Cambia el modo del ACTIVO (real vs provisional), independiente del cliente. */
  toggleAssetMode(useProvisional: boolean): void {
    this.useProvisionalAsset = useProvisional;
    this.updateFormValidations();
  }

  /**
   * Actualiza las validaciones del formulario según el modo actual
   */
  updateFormValidations(): void {
    // Cliente (real vs provisional) — gobernado por useProvisionalData.
    if (this.useProvisionalData) {
      this.entity.get('client')?.clearValidators();
      this.entity.get('provitionalClient')?.setValidators([Validators.required]);
    } else {
      this.entity.get('provitionalClient')?.clearValidators();
      this.entity.get('client')?.setValidators([Validators.required]);
    }
    // Activo (real vs provisional) — gobernado por useProvisionalAsset (independiente).
    if (this.useProvisionalAsset) {
      this.entity.get('assets')?.clearValidators();
      this.entity.get('provitionalAsset')?.setValidators([Validators.required]);
      this.entity.get('provitionalValue')?.setValidators([Validators.required, Validators.min(1)]);
    } else {
      this.entity.get('provitionalAsset')?.clearValidators();
      this.entity.get('provitionalValue')?.clearValidators();
      this.entity.get('assets')?.setValidators([Validators.required]);
    }

    // Actualizar validadores
    this.entity.get('client')?.updateValueAndValidity();
    this.entity.get('assets')?.updateValueAndValidity();
    this.entity.get('provitionalClient')?.updateValueAndValidity();
    this.entity.get('provitionalAsset')?.updateValueAndValidity();
    this.entity.get('provitionalValue')?.updateValueAndValidity();
  }

  /**
   * Inicializa el formulario según el método y detecta si usar datos provisionales
   */
  initializeForm(): void {
    // Detectar si hay datos provisionales existentes en edición
    if (this.formMethod === 'EDIT' && this.quote) {
      this.useProvisionalData = !this.quote.client || this.quote.provitionalClient;
      this.useProvisionalAsset = !this.quote.assets?.length || !!this.quote.provitionalAsset;
      // Regla: cliente provisional ⟹ activo provisional (combinación real/provisional inválida).
      if (this.useProvisionalData) this.useProvisionalAsset = true;

      // Cargar TODOS los activos disponibles para que el usuario pueda agregar más
      this.fetchAssetsWithMerge();
    }
    
    this.createUnifiedForm();
    this.updateFormValidations();
  }

  /**
   * Carga todos los activos disponibles y hace merge con los de la cotización actual
   * Esto permite que activos ya asignados (con status diferente a 'Activo') sigan visibles
   */
  private fetchAssetsWithMerge(): void {
    this.apiQuoteParametersService.fetchAssets({ page: 1, limit: 1000, filters: {} }).subscribe(
      (availableAssets: any[]) => {
        // Empezar con los activos disponibles del sistema
        const assetsMap = new Map<string, { label: string; value: string; amount: number }>();
        
        // Agregar todos los activos disponibles
        availableAssets.forEach(asset => {
          assetsMap.set(asset.value, asset);
        });
        
        // Agregar/actualizar con los activos de la cotización actual (pueden tener otro status)
        if (this.quote?.assets && this.quote.assets.length > 0) {
          this.quote.assets.forEach((asset: any) => {
            if (!assetsMap.has(asset._id)) {
              assetsMap.set(asset._id, {
                label: asset.name,
                value: asset._id,
                amount: asset.acquisition?.total || 0
              });
            }
          });
        }
        
        // Convertir el Map a array
        this.actives = Array.from(assetsMap.values());
        
        // Recalcular el valor total de activos después de cargar
        this.recalculateAssetsTotalValue();
      },
      error => {
        console.error('Error al obtener los activos:', error);
        // Fallback: usar solo los activos de la cotización
        if (this.quote?.assets && this.quote.assets.length > 0) {
          this.actives = this.quote.assets.map((asset: any) => ({
            label: asset.name,
            value: asset._id,
            amount: asset.acquisition?.total || 0
          }));
          // Recalcular también en el fallback
          this.recalculateAssetsTotalValue();
        }
      }
    );
  }

  /**
   * Recalcula el valor total basado en los activos seleccionados en el formulario
   */
  private recalculateAssetsTotalValue(): void {
    const selectedAssetIds: string[] = this.entity.get('assets')?.value || [];
    
    if (selectedAssetIds.length > 0 && this.actives.length > 0) {
      const totalValue = selectedAssetIds.reduce((sum: number, id: string) => {
        const asset = this.actives.find(a => a.value === id);
        return sum + (asset?.amount || 0);
      }, 0);
      
      if (totalValue > 0) {
        this.entity.get('provitionalValue')?.setValue(totalValue, { emitEvent: false });
      }
    }
  }

  /**
   * Crea un formulario unificado que maneja ambos modos
   */
  private createUnifiedForm(): void {
    // Calcular fecha actual para inicio del arrendamiento
    const currentDate = this.getCurrentDate();

    // Reset del catálogo de plazos al del rol (36/48/60; Admin suma 12/24). En EDIT, populateEditForm
    // re-inyecta los plazos legacy de la cotización como opciones deshabilitadas.
    this.termInMonths = this.termOptions;

    this.entity = this.fb.group({
      case: [this.formMethod === 'EDIT' ? 'EDIT' : 'CREATE'],
      // Campos para datos reales
      client: [null],
      assets: [[] as string[]],
      // Campos para datos provisionales
      provitionalClient: [''],
      provitionalAsset: [''],
      provitionalValue: [0],
      // Campos comunes
      leaseType: ['Puro', [Validators.required]], // Siempre "Puro" por defecto
      terms: [[], [Validators.required]],
      paymentStart: [currentDate, [Validators.required]], // Fecha actual automática
      advance: [5, [Validators.required]],
      interestRate: [25, [Validators.required]],
      provisionPe: [12],
      openingCommission: [3, [Validators.required]],
      advancedRents: [0, [Validators.required]],
      ratificationExpenses: [3500, [Validators.required]],
      accessories: this.fb.array([])
    });

    // Poblar datos existentes en modo edición
    if (this.formMethod === 'EDIT' && this.quote) {
      this.populateEditForm();
    }
  }

  /**
   * Obtiene la fecha current en formato ISO (YYYY-MM-DD)
   */
  private getCurrentDate(): string {
    return new Date().toISOString().substring(0, 10);
  }

  /**
   * Pobla el formulario con datos existentes en modo edición
   */
  private populateEditForm(): void {
    const paymentStartDate = this.quote.createdAt ? new Date(this.quote.createdAt).toISOString().substring(0, 10) : '';

    this.entity.patchValue({
      leaseType: this.quote.leaseType,
      paymentStart: paymentStartDate,
      advance: this.quote.advance?.percentage ? (this.quote.advance.percentage * 100) : this.quote.advance,
      interestRate: this.quote.interestRate,
      provisionPe: this.quote.provisionPe,
      openingCommission: this.quote.openingCommission?.percentage ? (this.quote.openingCommission.percentage * 100) : this.quote.openingCommission,
      advancedRents: this.quote.advancedRents,
      ratificationExpenses: this.quote.ratificationExpenses,
      provitionalValue: this.quote.provitionalValue
    });

    // Poblar el CLIENTE según su modo.
    if (this.useProvisionalData) {
      this.entity.patchValue({ provitionalClient: this.quote.provitionalClient });
    } else {
      this.entity.patchValue({ client: this.quote.client?._id || this.quote.client });

      // Sembrar el cliente seleccionado en las opciones: puede ser un cliente antiguo
      // que no aparece en la búsqueda inicial (primeros 50). Sin esto, el dropdown no
      // mostraría su nombre al editar la cotización.
      if (this.quote.client && typeof this.quote.client === 'object' && this.quote.client._id) {
        const c = this.quote.client
        if (!this.customers.some(x => x.value === c._id)) {
          this.customers = [{ label: c.name, value: c._id, rfc: c.rfc }, ...this.customers]
        }
      }
    }

    // Poblar el ACTIVO según su modo (independiente del cliente).
    if (this.useProvisionalAsset) {
      this.entity.patchValue({
        provitionalAsset: this.quote.provitionalAsset,
        provitionalValue: this.quote.provitionalValue,
      });
    } else {
      const mappedAssets = this.quote.assets?.map((asset: any) => asset._id || asset) || [];
      this.entity.patchValue({ assets: mappedAssets });
      // El recálculo del valor total se hace en fetchAssetsWithMerge() → recalculateAssetsTotalValue()
      // que se ejecuta al completar la carga de activos, evitando race conditions.
    }

    // Poblar términos
    if (this.quote.selectedTerms || this.quote.terms) {
      const terms = this.quote.selectedTerms || this.quote.terms;
      const mappedTerms = terms.map((term: any) => ({
        term: term.term,
        residuary: term.residuary?.percentage ? (term.residuary.percentage * 100) : term.residuary
      }));

      // Mostrar plazos que el rol no puede elegir como opciones deshabilitadas.
      this.injectLegacyTermOptions(mappedTerms.map((t: any) => t.term));

      this.termsArray.clear();
      mappedTerms.forEach((term: any) => {
        this.termsArray.push(this.fb.group({
          term: [term.term, Validators.required],
          residuary: [term.residuary, [Validators.required, Validators.min(this.role === UserRole.BROKER ? 5 : 0), Validators.max(this.role === UserRole.BROKER ? 30 : 100)]]
        }));
      });
      
      this.entity.get('terms')?.setValue(mappedTerms.map((t: any) => t.term));
    }

    // Poblar accesorios
    if (this.quote.accessories) {
      const accessoriesArray = this.entity.get('accessories') as FormArray;
      accessoriesArray.clear();
      this.quote.accessories.forEach((accessory: any) => {
        accessoriesArray.push(this.fb.group({
          name: [accessory.name, [Validators.required]],
          paymentType: [accessory.paymentType, [Validators.required]],
          interestRate: [accessory.interestRate || null],
          amount: [accessory.amount, [Validators.required]],
          isInsurance: [accessory.isInsurance || false],
          insuranceDetails: this.fb.group({
            startPeriod: [accessory.insuranceDetails?.startPeriod || null],
            endPeriod: [accessory.insuranceDetails?.endPeriod || null],
            notifyBeforeExpiry: [accessory.insuranceDetails?.notifyBeforeExpiry || 0],
            provider: [accessory.insuranceDetails?.provider || ''],
            policyNumber: [accessory.insuranceDetails?.policyNumber || ''],
            renewalType: [accessory.insuranceDetails?.renewalType || ''],
          })
        }));
      });
    }
  }
}